import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useU2000LogicalUnitContext } from '../../../u2000Core/context/logicalUnit/U2000LogicalUnitContext';
import { NS } from '../../U3204';
import { useU3204PollU2200JobHandlers } from '../../hooks/useU3204PollU2200JobHandlers';
import { useU3204ProductHelpReindexHandlers } from '../../hooks/useU3204ProductHelpReindexHandlers';
import { useU3204ProductHelpSourcesHandlers } from '../../hooks/useU3204ProductHelpSourcesHandlers';
import type { U3204ProductHelpIndexCounters, U3204U2200Job } from '../../U3204Service';
import { u3204FormatIndexedAtOrg, u3204TruncateHash } from '../../utils/u3204ProductHelpAdminFormat';

function extractCounters(job: U3204U2200Job | null): U3204ProductHelpIndexCounters | null {
  if (!job?.result) return null;
  const data = job.result.data;
  if (data && typeof data === 'object') return data;
  return null;
}

const U3204ProductHelpAdminScreen: React.FC = () => {
  const { t } = useTranslation(NS);
  const { setLogicalUnitTitle, setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  const { sources, loading, loadSources } = useU3204ProductHelpSourcesHandlers();
  const { submitting, startReindex } = useU3204ProductHelpReindexHandlers();
  const { pollUntilDone } = useU3204PollU2200JobHandlers();

  const [ulFilter, setUlFilter] = useState('');
  const [job, setJob] = useState<U3204U2200Job | null>(null);
  const [jobRunning, setJobRunning] = useState(false);

  useEffect(() => {
    setLogicalUnitTitle(t('admin.sourcesTitle'));
    setLogicalUnitBreadcrumb([
      { href: '/U3204', label: t('title') },
      { label: t('admin.sourcesTitle') },
    ]);
    return () => {
      setLogicalUnitTitle(t('title'));
      setLogicalUnitBreadcrumb([]);
    };
  }, [setLogicalUnitBreadcrumb, setLogicalUnitTitle, t]);

  useEffect(() => {
    void loadSources();
  }, [loadSources]);

  const ulOptions = useMemo(() => {
    const codes = new Set(sources.map((s) => s.logicalUnitCode).filter(Boolean));
    return Array.from(codes).sort();
  }, [sources]);

  const busy = loading || submitting || jobRunning;

  const handleReindex = async () => {
    const body = ulFilter.trim() ? { logicalUnitCode: ulFilter.trim() } : {};
    const created = await startReindex(body);
    if (!created) return;
    setJob(created);
    setJobRunning(true);
    const terminal = await pollUntilDone(created.id, (update) => setJob(update));
    setJobRunning(false);
    if (terminal) setJob(terminal);
    await loadSources();
  };

  const counters = extractCounters(job);
  const jobBanner =
    jobRunning || job?.status === 'pending' || job?.status === 'running'
      ? t('admin.jobRunning')
      : job?.status === 'failed'
        ? t('admin.jobFailed')
        : job?.status === 'completed'
          ? t('admin.jobDone')
          : null;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto p-4">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-lg font-semibold text-foreground">{t('admin.sourcesTitle')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('admin.sourcesHint')}</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-muted-foreground">{t('admin.filterUl')}</span>
            <select
              className="min-w-40 rounded-sm border border-input bg-background px-2 py-1.5 text-sm"
              value={ulFilter}
              onChange={(e) => setUlFilter(e.target.value)}
              disabled={busy}
            >
              <option value="">{t('admin.filterUlAll')}</option>
              {ulOptions.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="rounded-sm border border-input bg-background px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
            onClick={() => void loadSources()}
            disabled={busy}
          >
            {t('admin.refresh')}
          </button>
          <button
            type="button"
            className="rounded-sm bg-primary px-3 py-1.5 text-sm text-primary-foreground hover:opacity-90 disabled:opacity-50"
            onClick={() => void handleReindex()}
            disabled={busy}
          >
            {t('admin.reindex')}
          </button>
        </div>
      </header>

      {jobBanner ? (
        <div
          className={`rounded border px-3 py-2 text-sm ${
            job?.status === 'failed'
              ? 'border-destructive/40 bg-destructive/10 text-destructive'
              : job?.status === 'completed'
                ? 'border-emerald-500/30 bg-emerald-500/10 text-foreground'
                : 'border-border bg-muted/40 text-foreground'
          }`}
          role="status"
        >
          <p className="font-medium">{jobBanner}</p>
          {job?.errorMessage ? <p className="mt-1 text-xs">{job.errorMessage}</p> : null}
          {job?.result?.message ? <p className="mt-1 text-xs text-muted-foreground">{job.result.message}</p> : null}
          {counters ? (
            <p className="mt-1 text-xs text-muted-foreground">
              {t('admin.counters', {
                indexed: counters.indexed ?? 0,
                skipped: counters.skipped ?? 0,
                errors: counters.errors ?? 0,
                chunks: counters.chunksCreated ?? 0,
                embeddings: counters.embeddingsUpdated ?? 0,
              })}
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="min-h-0 overflow-x-auto rounded-sm border border-border">
        <table className="w-full min-w-160 border-collapse text-left text-sm">
          <thead className="bg-muted/50 text-muted-foreground">
            <tr>
              <th className="px-3 py-2 font-medium">{t('admin.col.ul')}</th>
              <th className="px-3 py-2 font-medium">{t('admin.col.revision')}</th>
              <th className="px-3 py-2 font-medium">{t('admin.col.hash')}</th>
              <th className="px-3 py-2 font-medium">{t('admin.col.indexedAt')}</th>
              <th className="px-3 py-2 font-medium">{t('admin.col.chunks')}</th>
              <th className="px-3 py-2 font-medium">{t('admin.col.path')}</th>
            </tr>
          </thead>
          <tbody>
            {loading && sources.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">
                  {t('admin.loading')}
                </td>
              </tr>
            ) : sources.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">
                  {t('admin.empty')}
                </td>
              </tr>
            ) : (
              sources.map((row) => (
                <tr key={row.id} className="border-t border-border">
                  <td className="px-3 py-2 font-medium">{row.logicalUnitCode}</td>
                  <td className="px-3 py-2 tabular-nums">{row.revision}</td>
                  <td className="px-3 py-2 font-mono text-xs" title={row.contentHash}>
                    {u3204TruncateHash(row.contentHash)}
                  </td>
                  <td className="px-3 py-2 tabular-nums">{u3204FormatIndexedAtOrg(row.indexedAt)}</td>
                  <td className="px-3 py-2 tabular-nums">{row.chunkCount}</td>
                  <td className="max-w-[20rem] truncate px-3 py-2 text-xs text-muted-foreground" title={row.sourcePath}>
                    {row.sourcePath}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default U3204ProductHelpAdminScreen;
